'use client';

import { useState } from 'react';
import { trips } from '@/data/mock-data';
import { CheckCircle, CreditCard, User, Calendar } from 'lucide-react';

type BookingStep = 'trip' | 'date' | 'details' | 'payment' | 'confirmation';

export default function BookingPage() {
  const [currentStep, setCurrentStep] = useState<BookingStep>('trip');
  const [selectedTrip, setSelectedTrip] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [guestCount, setGuestCount] = useState(1);
  const [ospreyAddon, setOspreyAddon] = useState(false);

  const steps: { id: BookingStep; label: string }[] = [
    { id: 'trip', label: 'Select Trip' },
    { id: 'date', label: 'Choose Date' },
    { id: 'details', label: 'Your Details' },
    { id: 'payment', label: 'Payment' },
    { id: 'confirmation', label: 'Confirmation' },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);
  const selectedTripData = trips.find((t) => t.id === selectedTrip);
  const depositAmount = selectedTripData ? (selectedTripData.price * guestCount * 0.5) + (ospreyAddon ? 50 : 0) : 0;

  const handleNextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].id);
    }
  };

  const handleSubmitBooking = () => {
    // Mock payment processing
    setCurrentStep('confirmation');
  };

  return (
    <div className="min-h-screen py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Book Your Adventure
            </h1>
            <p className="text-lg text-muted-foreground">
              Follow these steps to reserve your spot on an unforgettable backcountry trip
            </p>
          </div>

          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                        index <= currentStepIndex
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {index < currentStepIndex ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span className="text-xs mt-2 text-center hidden md:block">
                      {step.label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-1 flex-1 transition-colors ${
                        index < currentStepIndex ? 'bg-primary' : 'bg-muted'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-card p-8 rounded-2xl border border-border">
            {/* Step 1: Select Trip */}
            {currentStep === 'trip' && (
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Select Your Trip</h2>
                <div className="space-y-4">
                  {trips.map((trip) => (
                    <button
                      key={trip.id}
                      onClick={() => setSelectedTrip(trip.id)}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                        selectedTrip === trip.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-lg text-foreground mb-1">
                            {trip.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            {trip.location} • {trip.duration}
                          </p>
                          <span className="text-sm px-3 py-1 rounded-full bg-muted text-muted-foreground">
                            {trip.difficulty}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">${trip.price}</div>
                          <div className="text-xs text-muted-foreground">per person</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleNextStep}
                  disabled={!selectedTrip}
                  className="w-full mt-6 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue to Date Selection
                </button>
              </div>
            )}

            {/* Step 2: Choose Date */}
            {currentStep === 'date' && (
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Choose Your Date</h2>
                <p className="text-muted-foreground mb-6">
                  Select a date for your {selectedTripData?.title} adventure:
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      <Calendar className="inline w-4 h-4 mr-2" />
                      Trip Start Date
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      <User className="inline w-4 h-4 mr-2" />
                      Number of Guests
                    </label>
                    <select
                      value={guestCount}
                      onChange={(e) => setGuestCount(parseInt(e.target.value))}
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                    >
                      {[...Array(selectedTripData?.maxCapacity || 1)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1} {i === 0 ? 'Guest' : 'Guests'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <button
                    onClick={() => setCurrentStep('trip')}
                    className="flex-1 px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors font-semibold"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleNextStep}
                    disabled={!selectedDate}
                    className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Details
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Your Details */}
            {currentStep === 'details' && (
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Your Information</h2>
                <form className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Emergency Contact Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Emergency Contact Phone *
                    </label>
                    <input
                      type="tel"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Experience Level
                    </label>
                    <select className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground">
                      <option>First time backpacker</option>
                      <option>Some experience (1-5 trips)</option>
                      <option>Experienced (6+ trips)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-accent/10 rounded-lg">
                    <input
                      type="checkbox"
                      id="osprey"
                      checked={ospreyAddon}
                      onChange={(e) => setOspreyAddon(e.target.checked)}
                      className="w-5 h-5"
                    />
                    <label htmlFor="osprey" className="text-foreground">
                      Add Osprey Backpack (+$50) - Brand new backpack to take home!
                    </label>
                  </div>
                </form>
                <div className="flex gap-4 mt-6">
                  <button
                    onClick={() => setCurrentStep('date')}
                    className="flex-1 px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors font-semibold"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleNextStep}
                    className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold"
                  >
                    Continue to Payment
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Payment */}
            {currentStep === 'payment' && (
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Payment Information</h2>
                <div className="bg-accent/10 p-6 rounded-xl mb-6">
                  <h3 className="font-semibold text-foreground mb-4">Booking Summary</h3>
                  <div className="space-y-2 text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Trip:</span>
                      <span className="text-foreground">{selectedTripData?.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Date:</span>
                      <span className="text-foreground">{selectedDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Guests:</span>
                      <span className="text-foreground">{guestCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Price per person:</span>
                      <span className="text-foreground">${selectedTripData?.price}</span>
                    </div>
                    {ospreyAddon && (
                      <div className="flex justify-between">
                        <span>Osprey Backpack:</span>
                        <span className="text-foreground">$50</span>
                      </div>
                    )}
                    <div className="border-t border-border pt-2 mt-2">
                      <div className="flex justify-between font-semibold text-foreground text-lg">
                        <span>50% Deposit Due:</span>
                        <span className="text-primary">${depositAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-6">
                  <CreditCard className="inline w-4 h-4 mr-1" />
                  This is a demo booking form. No actual payment will be processed.
                </p>

                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Card Number
                    </label>
                    <input
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Expiry Date
                      </label>
                      <input
                        type="text"
                        placeholder="MM/YY"
                        className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">CVV</label>
                      <input
                        type="text"
                        placeholder="123"
                        className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Billing Zip Code
                    </label>
                    <input
                      type="text"
                      placeholder="12345"
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                    />
                  </div>
                </form>

                <div className="flex gap-4 mt-6">
                  <button
                    onClick={() => setCurrentStep('details')}
                    className="flex-1 px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors font-semibold"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmitBooking}
                    className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold"
                  >
                    Complete Booking
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Confirmation */}
            {currentStep === 'confirmation' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-secondary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground mb-4">Booking Confirmed!</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Thank you for booking your adventure! You'll receive a confirmation email shortly
                  with all the details and pre-trip information.
                </p>
                <div className="bg-card p-6 rounded-xl border border-border mb-8 max-w-md mx-auto">
                  <div className="space-y-2 text-left">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Trip:</span>
                      <span className="font-semibold">{selectedTripData?.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date:</span>
                      <span className="font-semibold">{selectedDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Guests:</span>
                      <span className="font-semibold">{guestCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Deposit Paid:</span>
                      <span className="font-semibold text-secondary">
                        ${depositAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  Questions? Contact Shane at{' '}
                  <a href="mailto:contact@backcountryadv.com" className="text-primary underline">
                    contact@backcountryadv.com
                  </a>
                </p>
                <button
                  onClick={() => window.location.href = '/'}
                  className="px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold"
                >
                  Return to Homepage
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
